import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type {
  IdResponse,
  TenantDeactivationImpactCore,
  TenantView as SharedTenantView,
  UpdateActiveRequest,
  UpdateTenantRestigoConfigRequest,
} from 'shared-models/browser';

export type TenantView = SharedTenantView & { photoUrl?: string | null };

export type CreateTenantRequest = {
  companyName: string;
};

export type UpdateTenantActiveRequest = UpdateActiveRequest;

export type UpdateTenantMaintenanceModeRequest = {
  maintenanceMode: boolean;
};

export type UpdateTenantOwnerEmailRequest = {
  ownerEmail: string | null;
};

export type TenantDeactivationImpact = TenantDeactivationImpactCore & {
  tenantIdPk: number;
};

class TenantsService {
  // Summary: Fetches tenants for the super-admin tenants tab and drives tenant administration visibility.
  // Details: docs/function-library/web/services.md#tenants-service
  async list(): Promise<TenantView[]> {
    try {
      const response = await authenticatedInstance.get<TenantView[]>('/tenants');
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin tenant provisioning requests to backend transactional tenant bootstrap flow.
  // Details: docs/function-library/web/services.md#tenants-service
  async create(payload: CreateTenantRequest): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>('/tenants', payload);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin tenant activation state updates while keeping tenant identity immutable.
  // Details: docs/function-library/web/services.md#tenants-service
  async updateActive(tenantIdPk: number, payload: UpdateTenantActiveRequest): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/tenants/${tenantIdPk}/active`,
        payload,
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async updateMaintenanceMode(
    tenantIdPk: number,
    payload: UpdateTenantMaintenanceModeRequest,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/maintenance-mode`, payload);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון מצב התחזוקה נכשל'));
    }
  }

  // Summary: Updates only the tenant VAT rate (super-admin); SSE patches the canonical row.
  async updateVatRate(tenantIdPk: number, vatRate: number): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/vat-rate`, { vatRate });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון המע"מ נכשל'));
    }
  }

  // Summary: Flags/unflags a tenant as an e2e sandbox (super-admin); SSE patches the canonical row.
  async updateIsTest(tenantIdPk: number, isTest: boolean): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/is-test`, { isTest });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון סימון חברת בדיקה נכשל'));
    }
  }

  async updateHeader(tenantIdPk: number, header: string | null): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/header`, { header });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון הכותרת נכשל'));
    }
  }

  async updateOwnerEmail(
    tenantIdPk: number,
    payload: UpdateTenantOwnerEmailRequest,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/owner-email`, payload);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון מייל בעלים נכשל'));
    }
  }

  // Summary: Sets/clears the tenant Restigo branch_id + key (super-admin); SSE patches the row.
  async updateRestigoConfig(
    tenantIdPk: number,
    payload: UpdateTenantRestigoConfigRequest,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/restigo-config`, payload);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון פרטי רסטיגו נכשל'));
    }
  };
  // Sets/clears the Ontopo key (raw sent once, never returned); SSE patches the masked row.
  async updateOntopoKey(tenantIdPk: number, ontopoKey: string | null): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/tenants/${tenantIdPk}/ontopo-key`, { ontopoKey });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון מפתח אונטופו נכשל'));
    }
  }

  // Uploads a tenant logo; backend normalizes and stores it, SSE patches the row.
  async uploadPhoto(tenantIdPk: number, photoFile: File): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      await authenticatedInstance.post<void>(`/tenants/${tenantIdPk}/photo`, formData);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'העלאת תמונת החברה נכשלה'));
    }
  }

  // Summary: Clears the tenant logo (never deletes the tenant); SSE patches the row.
  async removePhoto(tenantIdPk: number): Promise<void> {
    try {
      await authenticatedInstance.delete<void>(`/tenants/${tenantIdPk}/photo`);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'הסרת תמונת החברה נכשלה'));
    }
  }

  // Summary: Fetches tenant deactivation impact details for confirmation UX before applying potentially disruptive tenant status changes.
  // Details: docs/function-library/web/services.md#tenants-service
  async deactivationImpact(tenantIdPk: number): Promise<TenantDeactivationImpact> {
    try {
      const response = await authenticatedInstance.get<TenantDeactivationImpact>(
        `/tenants/${tenantIdPk}/deactivation-impact`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const tenantsService = new TenantsService();





